import { useRef, useState } from "react";
import { toast } from "sonner";
import { MailWarning, Hourglass, XCircle, Upload } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

export default function AccountStatusBanner() {
  const { user, refresh } = useAuth();
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef(null);
  if (!user || user.is_admin) return null;
  const action = user.role === "supplier" ? "fare offerte" : "pubblicare ordini";
  const pending = user.verification_status === "pending";
  const rejected = user.verification_status === "rejected";
  if (user.email_verified && !pending && !rejected) return null;

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data.message);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File troppo grande · massimo 5 MB"); return; }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await api.post("/me/visura", form);
      toast.success("Visura caricata: l'amministratore la controlla e ti avvisa via email");
      await refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  return (
    <div className="no-print mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-4 space-y-3" data-testid="account-status-banner">
      {!user.email_verified && (
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="email-unverified-banner">
          <MailWarning className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm text-amber-900">
            <div className="font-semibold">Conferma la tua email</div>
            <div className="text-xs mt-0.5">Ti abbiamo inviato un link a <b>{user.email}</b>: aprilo entro 48 ore. Finché non confermi l'email puoi usare l'app ma non {action}.</div>
          </div>
          <Button size="sm" variant="outline" className="rounded-full shrink-0" onClick={resend} disabled={sending} data-testid="resend-verification-button">
            {sending ? "Invio..." : "Invia di nuovo"}
          </Button>
        </div>
      )}
      {pending && (
        <div className="flex flex-col sm:flex-row sm:items-start gap-3 rounded-2xl border border-sky-200 bg-sky-50 p-4" data-testid="account-pending-banner">
          <Hourglass className="w-5 h-5 text-sky-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm text-sky-900">
            <div className="font-semibold">{user.role === "supplier" ? "Account fornitore in attesa di approvazione" : "Azienda in attesa di verifica"}</div>
            {user.vies_valid ? (
              <div className="text-xs mt-0.5">La tua Partita IVA risulta su VIES: l'amministratore controlla i dati e approva l'account. Potrai {action} appena approvato: ti avvisiamo via email.</div>
            ) : (
              <div className="text-xs mt-0.5">
                La tua Partita IVA non risulta su VIES, oppure il servizio non era raggiungibile. Carica la <b>visura camerale</b> (PDF, JPEG o PNG, massimo 5 MB): l'amministratore la controlla e approva l'account a mano. Potrai {action} appena approvato.
                {user.visura_uploaded_at && <span className="block mt-1 font-semibold" data-testid="visura-uploaded-at">Visura caricata il {new Date(user.visura_uploaded_at).toLocaleString("it-IT")}.</span>}
              </div>
            )}
          </div>
          {!user.vies_valid && (
            <>
              <input ref={fileInput} type="file" accept="application/pdf,image/jpeg,image/png" className="hidden" onChange={upload} data-testid="visura-file-input" />
              <Button size="sm" className="ga-btn-primary rounded-full shrink-0" onClick={() => fileInput.current?.click()} disabled={uploading} data-testid="visura-upload-button">
                <Upload className="w-4 h-4 mr-1.5" />{uploading ? "Caricamento..." : user.visura_uploaded_at ? "Sostituisci visura" : "Carica visura camerale"}
              </Button>
            </>
          )}
        </div>
      )}
      {rejected && (
        <div className="flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="account-rejected-banner">
          <XCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <div className="font-semibold">Verifica dell'azienda non approvata</div>
            <div className="text-xs mt-0.5">{user.verification_reason && <>Motivo: {user.verification_reason}. </>}Non puoi {action}. Per chiarimenti contatta l'amministratore.</div>
          </div>
        </div>
      )}
    </div>
  );
}
